// client/src/components/CartFormModal.jsx
import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Stack,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
} from "@mui/material";

import emailjs from "@emailjs/browser";

// ---------- utils ----------
const money = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Simple HTML escaper for safe email HTML rows
const escapeHtml = (s = "") =>
  String(s).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[c])
  );

// Provinces & territories (kept for address but tax disabled)
const PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

const CA_POSTAL_PATTERN = "[A-Za-z]\\d[A-Za-z][ ]?\\d[A-Za-z]\\d";
const normalizePostal = (s) => {
  const up = (s || "").toUpperCase().replace(/\s+/g, "");
  if (up.length === 6) return `${up.slice(0, 3)} ${up.slice(3)}`;
  return s;
};

const genOrderId = () =>
  `ORD-${Date.now().toString(36).toUpperCase()}-${Math.random()
    .toString(36)
    .slice(2, 6)
    .toUpperCase()}`;

// Bottle weights for shipping estimate
const bottleWeightLbs = (item) => {
  const size = String(item.size || "");
  if (/^\s*5\s*oz\s*$/i.test(size)) return 1;
  if (/^\s*12\s*oz\s*$/i.test(size)) return 1.5;
  return 0; // unknown sizes count as 0
};

// ---------- component ----------
export default function CartFormModal({ open, onClose, cart = [] }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    province: "ON",
    postal: "",
    country: "Canada",
    delivery_method: "delivery", // delivery | pickup
    accept_subs: true,
    notes: "",
  });

  // CRA envs only (ensure .env has these with REACT_APP_ prefix)
  const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
  const TEMPLATE_ID_ORDER = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_ORDER;
  const TEMPLATE_ID_CONFIRM = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_CONFIRM; // optional

  const onChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onPostalBlur = () =>
    setForm((f) => ({ ...f, postal: normalizePostal(f.postal) }));

  const subtotal = useMemo(
    () =>
      cart.reduce(
        (acc, item) => acc + num(item.price) * (item.quantity ?? 1),
        0
      ),
    [cart]
  );

  const estimatedWeightLbs = useMemo(
    () =>
      cart.reduce(
        (acc, item) => acc + bottleWeightLbs(item) * (item.quantity ?? 1),
        0
      ),
    [cart]
  );

  // Shipping rules:
  // - pickup: $0
  // - delivery: subtotal < 100 => $20, else $0
  const shipping = useMemo(() => {
    if (form.delivery_method === "pickup") return 0;
    if (subtotal <= 0) return 0;
    return subtotal < 100 ? 20 : 0;
  }, [form.delivery_method, subtotal]);

  // Tax removed for now
  const tax = 0;
  const total = subtotal + shipping + tax;

  // Text summary (for reference / optional template blocks)
  const orderLinesText = useMemo(() => {
    if (!cart.length) return "No items.";
    return cart
      .map((item) => {
        const q = item.quantity ?? 1;
        const unit = num(item.price);
        const line = unit * q;
        const size = item.size ? ` (${item.size})` : "";
        const w = bottleWeightLbs(item);
        const weightNote = w ? ` • est. ${w} lb each` : "";
        return `• ${item.name}${size} × ${q} — ${money.format(
          line
        )}${weightNote}`;
      })
      .join("\n");
  }, [cart]);

  // HTML rows (Item | Qty | Total) for the email template's <tbody>{{{order_lines_html}}}</tbody>
  const orderLinesHtml = useMemo(() => {
    if (!cart.length) {
      return `<tr><td colspan="3" style="padding:10px 0;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#666;">No items.</td></tr>`;
    }
    return cart
      .map((item) => {
        const q = item.quantity ?? 1;
        const unit = num(item.price);
        const line = unit * q;
        const sizeHtml = item.size
          ? ` — <span style="color:#666;">${escapeHtml(item.size)}</span>`
          : "";
        return `
          <tr>
            <td style="padding:10px 0;font-family:Arial,Helvetica,sans-serif;font-size:14px;">
              ${escapeHtml(item.name)}${sizeHtml}
            </td>
            <td align="center" style="padding:10px 0;font-family:Arial,Helvetica,sans-serif;font-size:14px;">
              ${q}
            </td>
            <td align="right" style="padding:10px 0;font-family:Arial,Helvetica,sans-serif;font-size:14px;">
              <strong>${money.format(line)}</strong>
            </td>
          </tr>`;
      })
      .join("");
  }, [cart]);

  // Extra JS validation for conditional delivery fields
  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid email.";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 10) return "Please enter a valid phone number.";
    if (form.delivery_method === "delivery") {
      if (!form.address1.trim()) return "Please enter your street address.";
      if (!form.city.trim()) return "Please enter your city.";
      if (!form.province) return "Select your province/territory.";
      if (!new RegExp(`^${CA_POSTAL_PATTERN}$`, "i").test(form.postal))
        return "Enter a valid Canadian postal code (e.g., M5V 2T6).";
    }
    if (!cart.length) return "Your cart is empty.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) return setError(v);

    setSubmitting(true);
    try {
      const order_id = genOrderId();
      const created_at = new Date().toLocaleString("en-CA", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "America/Toronto",
      });

      const params = {
        // identity
        order_id,
        created_at,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        reply_to: form.email,
        from_name: form.full_name,

        // address
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        province: form.province,
        postal: form.postal,
        country: form.country,

        // order metadata
        delivery_method: form.delivery_method, // delivery | pickup
        accept_subs: form.accept_subs ? "Yes" : "No",
        notes: form.notes,

        // financials
        order_lines: orderLinesText,
        order_lines_html: orderLinesHtml,
        subtotal: money.format(subtotal),
        shipping: money.format(shipping),
        tax_rate_pct: "0%",
        tax: money.format(0),
        total: money.format(total),

        // additional
        shipping_eta: "3–5 business days",
        estimated_weight_lbs: String(estimatedWeightLbs),

        // branding referenced in template
        brand_name: "JamCan",
        support_email: "orders@jamcanhotpeppersauce.com",
        brand_address: "Toronto, ON",

        // raw cart for back-office
        cart_json: JSON.stringify(cart),
      };

      // NOTE: this is your *manual* order/email flow (EmailJS).
      // If you also kick off Stripe Checkout from the cart, be sure to POST
      // deliveryMethod to /api/checkout/session so shipping matches server-side.
      if (!SERVICE_ID || !TEMPLATE_ID_ORDER || !PUBLIC_KEY) {
        throw new Error(
          "Missing EmailJS env vars. Ensure REACT_APP_EMAILJS_* are set."
        );
      }

      // Admin / store-side notification
      await emailjs.send(SERVICE_ID, TEMPLATE_ID_ORDER, params, {
        publicKey: PUBLIC_KEY,
      });

      // Optional: customer confirmation
      // if (TEMPLATE_ID_CONFIRM) {
      //   await emailjs.send(SERVICE_ID, TEMPLATE_ID_CONFIRM, params, {
      //     publicKey: PUBLIC_KEY,
      //   });
      // }

      alert(
        "Thanks! Your order was submitted. We’ll reach out to confirm delivery & payment."
      );
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(
        "Sorry, we couldn’t submit your order right now. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Checkout</DialogTitle>

      {/* Remove noValidate to allow native HTML5 validation */}
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Your Order
            </Typography>

            <Box component="ul" sx={{ mt: 0, mb: 1, pl: 3 }}>
              {cart.map((item) => (
                <li key={`${item.id}-${item.size || "default"}`}>
                  {item.name} {item.size ? `(${item.size}) ` : ""}×{" "}
                  {item.quantity ?? 1}
                </li>
              ))}
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Subtotal</Typography>
              <Typography>{money.format(subtotal)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.secondary">Shipping</Typography>
              <Typography>{money.format(shipping)}</Typography>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 0.5 }}
            >
              <Typography fontWeight={600}>Total</Typography>
              <Typography fontWeight={600}>{money.format(total)}</Typography>
            </Stack>

            <Typography variant="caption" sx={{ display: "block", mt: 0.75 }}>
              Standard delivery in <strong>3–5 business days</strong>.{" "}
              <strong>Free shipping on orders $100+</strong>. Est. shipment
              weight: <strong>{estimatedWeightLbs} lb</strong> (5oz = 1 lb, 12oz
              = 1.5 lb).
            </Typography>
          </Box>

          {/* Contact */}
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Full name"
                fullWidth
                required
                inputProps={{ minLength: 2, autoComplete: "name" }}
                value={form.full_name}
                onChange={onChange("full_name")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                inputProps={{ autoComplete: "email" }}
                value={form.email}
                onChange={onChange("email")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Phone"
                fullWidth
                required
                value={form.phone}
                onChange={onChange("phone")}
                placeholder="e.g., 416-555-0123"
                inputProps={{
                  inputMode: "tel",
                  pattern: "^[0-9\\s()+-]{10,}$",
                  title: "Enter at least 10 digits.",
                  autoComplete: "tel",
                }}
              />
            </Grid>

            {/* Delivery method */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Fulfillment"
                select
                fullWidth
                value={form.delivery_method}
                onChange={onChange("delivery_method")}
              >
                <MenuItem value="delivery">Delivery</MenuItem>
                <MenuItem value="pickup">Pickup</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.accept_subs}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, accept_subs: e.target.checked }))
                    }
                  />
                }
                label="Allow substitutions if an item is out of stock"
              />
            </Grid>

            {/* Address (only required if delivery) */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {form.delivery_method === "pickup"
                  ? "Pickup contact info"
                  : "Delivery address"}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Address line 1"
                fullWidth
                required={form.delivery_method === "delivery"}
                value={form.address1}
                onChange={onChange("address1")}
                disabled={form.delivery_method === "pickup"}
                inputProps={{ autoComplete: "address-line1" }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Address line 2 (optional)"
                fullWidth
                value={form.address2}
                onChange={onChange("address2")}
                disabled={form.delivery_method === "pickup"}
                inputProps={{ autoComplete: "address-line2" }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="City"
                fullWidth
                required={form.delivery_method === "delivery"}
                value={form.city}
                onChange={onChange("city")}
                disabled={form.delivery_method === "pickup"}
                inputProps={{ autoComplete: "address-level2" }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Province / Territory"
                select
                fullWidth
                required={form.delivery_method === "delivery"}
                value={form.province}
                onChange={onChange("province")}
                disabled={form.delivery_method === "pickup"}
                inputProps={{ autoComplete: "address-level1" }}
              >
                {PROVINCES.map((p) => (
                  <MenuItem key={p.code} value={p.code}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Postal code"
                fullWidth
                required={form.delivery_method === "delivery"}
                value={form.postal}
                onChange={onChange("postal")}
                onBlur={onPostalBlur}
                placeholder="M5V 2T6"
                inputProps={{
                  maxLength: 7,
                  pattern: CA_POSTAL_PATTERN,
                  title: "Format: A1A 1A1",
                  autoComplete: "postal-code",
                }}
                disabled={form.delivery_method === "pickup"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Country"
                fullWidth
                value={form.country}
                onChange={onChange("country")}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notes (delivery instructions, buzz code, allergies, etc.)"
                fullWidth
                multiline
                rows={3}
                value={form.notes}
                onChange={onChange("notes")}
              />
            </Grid>
          </Grid>

          {error && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mt: 1 }}
              aria-live="polite"
            >
              {error}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Order"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
